import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import { Button, Container, Table, Spinner } from "react-bootstrap";
import { IoMdEye } from "react-icons/io";
import NavMenu from "../components/NavMenu";
import axios from "axios";
import photo from "../img/no-data.svg";
import { useNavigate } from "react-router-dom";
import FloatingActionMenu from "../components/FloatingActionButton";
import { FaEdit } from "react-icons/fa";
import { GiPadlock } from "react-icons/gi";

const getStatusStyle = (status) => {
  switch ((status || "").toLowerCase()) {
    case "submitted":
      return {
        bg: "#e6ffe6",
        text: "#2e7d32",
        dot: "#4caf50",
      };
    case "employee signed":
      return {
        bg: "#d0f0fd",
        text: "#0077b6",
        dot: "#0096c7",
      };
    case "both signed":
      return {
        bg: "#f3e5f5",
        text: "#6a1b9a",
        dot: "#9c27b0",
      };
    case "reviewing":
      return {
        bg: "#fff3e0",
        text: "#ef6c00",
        dot: "#ff9800",
      };
    case "pending":
      return {
        bg: "#ffebee",
        text: "#c62828",
        dot: "#f44336",
      };
    default:
      return {
        bg: "#eceff1",
        text: "#546e7a",
        dot: "#90a4ae",
      };
  }
};

function MyAppraisal() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noSignature, setNoSignature] = useState(false);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  const handleView = (item) => {
    // console.log(item.unique_doc_id);
    navigate(`/edit-assessment`, {
      state: {
        staff_id: item.staff_id,
        send_id: item.send_id,
        unique_doc_id: item.unique_doc_id,
      },
    });
  };

  useEffect(() => {
    const checkUserSignature = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("authToken");

      if (!user?.id || !token) return;

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API}/getSignature`,
          {
            params: { user_id: user.id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (res.data.success && res.data.data.length === 0) {
          setNoSignature(true);
        }
      } catch (err) {
        console.error("Error checking signature:", err);
      }
    };

    checkUserSignature();
  }, []);

  useEffect(() => {
    const fetchStaff = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        const staff_id = storedUser?.id || "";

        if (!staff_id) {
          console.error("No staff ID found.");
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API}/getStaffSendAppraisal`,
          {
            params: { staff_id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.data.success) {
          setStaff(response.data.data);
        } else {
          console.error(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching staff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStaff();
  }, []);

  const handleSubmit = (role_id, send_id, unique_doc_id) => {
    console.log(
      "Selected role_id:",
      role_id,
      "send_id:",
      send_id,
      unique_doc_id
    );
    navigate(`/submit-appraisal/${role_id}`, {
      state: { send_id, unique_doc_id },
    });
  };

  return (
    <>
      <NavMenu />
      <Container
        fluid
        className={`min-vh-100 p-4 ${isDark ? "text-light" : "text-dark"}`}
        style={{ paddingBottom: "80px" }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
          <h5 className="header-title mb-2 mb-md-0 fw-bold">My Appraisal</h5>
        </div>
        {loading ? (
          <div className="text-center">
            <Spinner
              animation="border"
              variant={isDark ? "light" : "primary"}
            />
          </div>
        ) : (
          <Table
            hover
            responsive
            className={`align-middle ${isDark ? "table-dark" : ""}`}
          >
            <thead>
              <tr className="text-center">
                <th>No</th>
                <th>Name</th>
                <th>Department</th>
                <th>Appraisal</th>
                <th>Supervisor</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {staff.length > 0 ? (
                staff.map((emp, index) => (
                  <tr key={index} className="align-middle text-center">
                    <td>{index + 1}</td>
                    <td>{emp.staff_name || "-"}</td>
                    <td>{emp.department || "-"}</td>
                    <td>{emp.appraisal_name || "-"}</td>
                    <td>{emp.supervisor_name || "-"}</td>
                    <td>
                      {(() => {
                        const { bg, text, dot } = getStatusStyle(emp.status);
                        return (
                          <div
                            className="d-inline-flex align-items-center px-2 py-1 rounded-pill"
                            style={{
                              backgroundColor: bg,
                              color: text,
                              fontWeight: "500",
                              textTransform: "capitalize",
                              fontSize: "12px",
                            }}
                          >
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                backgroundColor: dot,
                                borderRadius: "50%",
                                marginRight: "8px",
                              }}
                            ></div>
                            {emp.status || "-"}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <div className="d-flex justify-content-center gap-2">
                        <Button
                          variant="outline-light"
                          size="sm"
                          style={{
                            color: "#B31E8D",
                            borderColor: "#B31E8D",
                            backgroundColor: isDark ? "#2c2c2c" : "transparent",
                          }}
                          onClick={() =>
                            handleSubmit(
                              emp.role_id,
                              emp.send_id,
                              emp.unique_doc_id
                            )
                          }
                        >
                          {emp.allow === 1 ? (
                            <>
                              <FaEdit size={18} />
                              &nbsp; View PA Assessment
                            </>
                          ) : (
                            <>
                              <IoMdEye size={20} />
                              &nbsp; View PA Assessment
                            </>
                          )}
                        </Button>
                        <Button
                          variant="light"
                          size="sm"
                          style={{
                            color: "#fff",
                            backgroundColor: "#B31E8D",
                            opacity: [
                              "submitted",
                              "employee signed",
                              "both signed",
                            ].includes(emp.status?.toLowerCase())
                              ? 0.5
                              : 1,
                          }}
                          disabled={[
                            "submitted",
                            "employee signed",
                            "both signed",
                          ].includes(emp.status?.toLowerCase())}
                          onClick={() => handleView(emp)}
                        >
                          {[
                            "submitted",
                            "employee signed",
                            "both signed",
                          ].includes(emp.status?.toLowerCase()) ? (
                            <>
                              <GiPadlock size={20} />
                              &nbsp;Locked
                            </>
                          ) : (
                            <>
                              <FaEdit size={20} />
                              &nbsp;Amend
                            </>
                          )}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="d-flex flex-column align-items-center">
                      <img
                        src={photo}
                        alt="Illustration"
                        className="img-fluid mb-3"
                        width="100px"
                      />
                      <div
                        className={` ${
                          isDark ? "text-secondary" : "text-muted"
                        }`}
                      >
                        No PA Form found
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </Container>
      <FloatingActionMenu />
      {noSignature && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "20px",
            zIndex: 9999,
            padding: "12px 20px",
            backgroundColor: "#fff3e0",
            color: "#000",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
          }}
        >
          ⚠️ You haven't added your signature yet. Please go to your profile and
          add it.{" "}
          <span
            onClick={() => navigate("/signature")}
            style={{
              color: "#B31E8D",
              cursor: "pointer",
              marginLeft: "8px",
            }}
          >
            Click here to save your signature
          </span>
        </div>
      )}
    </>
  );
}

export default MyAppraisal;
